import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Loader2, Mail, CheckCircle, FileText } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { clientService } from '../../services/clientService';
import { logger } from '../../utils/logger';
import UploadingModal from '../common/UploadingModal';
import '../confirmations/ExceptionsModal.css';

interface ExpiryNotificationModalProps {
  isOpen: boolean;
  trades: any[];              // selected trades with expiryState 'pending' or 'notified'
  ourCalculations: any[];     // our settlement calculations
  clientId: string;
  onClose: () => void;
  onSuccess: (notifiedIds: string[], agreedIds: string[], manualAcceptTrades: { tradeId: string; wePayAmount?: number; weReceiveAmount?: number }[]) => void;
}

interface AccountPreview {
  tradeNumber: string;
  ruleMatched: boolean;
  ruleName?: string;
  cargarAccountId?: string;
  abonarAccountId?: string;
  templateId?: string;
  outputFormat?: string;
}

interface BankAccountInfo {
  id: string;
  accountName: string;
  bankName: string;
  swiftCode: string;
  accountCurrency: string;
  accountNumber: string;
  isDefault: boolean;
  accountType?: 'nostro' | 'vostro';
  counterpartyId?: string;
  counterpartyName?: string;
}

const fmt = (n: number | null | undefined): string => {
  if (n == null) return '\u2014';
  return n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

/** Format for input display — preserves decimals when present */
const fmtInput = (n: number): string => {
  const decimals = (String(n).split('.')[1] || '').length;
  return n.toLocaleString('es-CL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatDate = (d: string | undefined): string => {
  if (!d) return '';
  if (/^\d{2}-\d{2}-\d{4}$/.test(d)) return d.replace(/-/g, '/');
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  return d;
};

const getBankRef = (trade: any): string =>
  trade.CounterpartySettlement?.CounterpartyTradeNumber || trade.bankTradeNumber || '';

/** Determine which account dropdowns a trade needs based on settlement type */
const getRequiredAccounts = (trade: any, ourCalc: any) => {
  const isNDF = trade.SettlementType === 'Compensaci\u00f3n';
  if (isNDF) {
    return (ourCalc?.WePayAmount || 0) > 0
      ? { needsCargar: true, needsAbonar: false, cargarCurrency: ourCalc?.WePayCurrency }
      : { needsCargar: false, needsAbonar: true, abonarCurrency: ourCalc?.WeReceiveCurrency };
  }
  return {
    needsCargar: true,
    needsAbonar: true,
    cargarCurrency: ourCalc?.WePayCurrency,
    abonarCurrency: ourCalc?.WeReceiveCurrency,
  };
};

const ExpiryNotificationModal: React.FC<ExpiryNotificationModalProps> = ({
  isOpen,
  trades,
  ourCalculations,
  clientId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [netPayments, setNetPayments] = useState(false);
  const [siGenerating, setSiGenerating] = useState(false);
  const [siDone, setSiDone] = useState(false);

  // Trades marked as agreed (counterparty confirmed our amounts)
  const [agreedTradeIds, setAgreedTradeIds] = useState<Set<string>>(new Set());

  // Counterparty proposed amounts (for notified trades — Phase 2)
  const [cptyAmounts, setCptyAmounts] = useState<Record<string, { wePay: string; weReceive: string }>>({});

  // Bank account state
  const [bankAccounts, setBankAccounts] = useState<BankAccountInfo[]>([]);
  const [accountPreviews, setAccountPreviews] = useState<Record<string, AccountPreview>>({});
  const [accountSelections, setAccountSelections] = useState<Record<string, { cargarAccountId?: string; abonarAccountId?: string }>>({});
  const [previewLoading, setPreviewLoading] = useState(false);

  const organizationName = user?.organization?.name || 'Company';
  const prefix = 'forwardMaturity.expiryNotification';

  // Check if any notified trades are present (Phase 2 mode)
  const hasNotifiedTrades = useMemo(() => trades.some(t => t.expiryState === 'notified'), [trades]);

  // Group trades by bank
  const groupedByBank = useMemo(() => {
    const groups: Record<string, { bankName: string; bankEmail: string; trades: any[] }> = {};
    for (const trade of trades) {
      const bankId = trade.CounterpartyId || 'unknown';
      if (!groups[bankId]) {
        groups[bankId] = {
          bankName: trade.CounterpartyName || bankId,
          bankEmail: trade.bankEmail || '',
          trades: [],
        };
      }
      groups[bankId].trades.push(trade);
    }
    return groups;
  }, [trades]);

  // Fetch account previews when modal opens
  useEffect(() => {
    if (!isOpen) {
      setBankAccounts([]);
      setAccountPreviews({});
      setAccountSelections({});
      setSiDone(false);
      setAgreedTradeIds(new Set());
      setCptyAmounts({});
      return;
    }

    if (trades.length === 0) return;

    const fetchPreviews = async () => {
      setPreviewLoading(true);
      try {
        const tradeNumbers = trades.map(tr => tr.TradeNumber);
        const resp = await clientService.previewSettlementAccounts(clientId, tradeNumbers);
        if (resp.success && resp.data) {
          setBankAccounts(resp.data.bankAccounts);

          const previewMap: Record<string, AccountPreview> = {};
          const selections: Record<string, { cargarAccountId?: string; abonarAccountId?: string }> = {};

          for (const p of resp.data.previews) {
            previewMap[p.tradeNumber] = p;
            if (p.ruleMatched) {
              selections[p.tradeNumber] = {
                cargarAccountId: p.cargarAccountId,
                abonarAccountId: p.abonarAccountId,
              };
            }
          }

          // For notified trades, prefer saved account selections over rule previews
          for (const trade of trades) {
            const saved = trade.notifiedAccounts;
            if (saved && (saved.cargarAccountId || saved.abonarAccountId)) {
              selections[trade.TradeNumber] = {
                cargarAccountId: saved.cargarAccountId || selections[trade.TradeNumber]?.cargarAccountId,
                abonarAccountId: saved.abonarAccountId || selections[trade.TradeNumber]?.abonarAccountId,
              };
            }
          }

          setAccountPreviews(previewMap);
          setAccountSelections(selections);
        }
      } catch (err) {
        logger.error('Failed to fetch settlement account previews:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Validate all required accounts are selected
  const allAccountsSelected = useMemo(() => {
    if (previewLoading) return false;
    // Only validate accounts for trades that aren't purely agreed-without-email
    const tradesToCheck = trades.filter(tr => !agreedTradeIds.has(tr.TradeNumber) || true);
    for (const trade of tradesToCheck) {
      const calc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
      const req = getRequiredAccounts(trade, calc);
      const sel = accountSelections[trade.TradeNumber];
      if (req.needsCargar && !sel?.cargarAccountId) return false;
      if (req.needsAbonar && !sel?.abonarAccountId) return false;
    }
    return true;
  }, [trades, ourCalculations, accountSelections, previewLoading, agreedTradeIds]);

  /** Render an account dropdown for a specific trade + side */
  const renderAccountDropdown = (
    tradeNumber: string,
    side: 'cargar' | 'abonar',
    currency: string | undefined,
    showLabel: boolean,
    counterpartyId?: string,
  ) => {
    const filtered = bankAccounts.filter(a => {
      if (a.accountCurrency !== currency) return false;
      const type = a.accountType || 'nostro';
      if (side === 'abonar') return type === 'nostro';
      if (type === 'nostro') return true;
      return type === 'vostro' && a.counterpartyId === counterpartyId;
    });
    const key = side === 'cargar' ? 'cargarAccountId' : 'abonarAccountId';
    const currentValue = accountSelections[tradeNumber]?.[key] || '';
    const label = side === 'cargar' ? t(`${prefix}.cargarAccount`) : t(`${prefix}.abonarAccount`);

    return (
      <div key={`${tradeNumber}-${side}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: showLabel ? '3px' : 0 }}>
        {showLabel && (
          <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>{label}</span>
        )}
        <select
          value={currentValue}
          onChange={(e) => {
            setAccountSelections(prev => ({
              ...prev,
              [tradeNumber]: { ...prev[tradeNumber], [key]: e.target.value || undefined },
            }));
          }}
          style={{
            flex: 1,
            padding: '3px 6px',
            fontSize: '12px',
            borderRadius: '4px',
            border: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="">{t(`${prefix}.selectAccount`)}</option>
          {filtered.length === 0 ? (
            <option disabled>{t(`${prefix}.noAccountsForCurrency`, { currency })}</option>
          ) : (
            filtered.map(acc => {
              const typeLabel = (acc.accountType || 'nostro') === 'vostro' ? ' [Vostro]' : '';
              return (
                <option key={acc.id} value={acc.id}>
                  {acc.bankName} - {acc.accountNumber} ({acc.accountCurrency}){typeLabel}
                </option>
              );
            })
          )}
        </select>
      </div>
    );
  };

  /**
   * Parse a user-entered amount string to number.
   * Handles both formats:
   *   - English decimal: "5233.33" or "1,964,000.50"
   *   - es-CL / European: "1.964.000" or "5233,33"
   * Rule: if the last separator (dot or comma) is followed by 1-2 digits, it's a decimal point.
   *        If followed by 3+ digits (or nothing after), it's a thousand separator.
   */
  const parseLocalizedNumber = (value: string): number => {
    const trimmed = value.replace(/\s/g, '');
    // Find the last dot or comma
    const lastDot = trimmed.lastIndexOf('.');
    const lastComma = trimmed.lastIndexOf(',');
    const lastSep = Math.max(lastDot, lastComma);

    if (lastSep === -1) {
      // No separators at all, plain integer
      return parseFloat(trimmed);
    }

    const afterSep = trimmed.substring(lastSep + 1);
    if (afterSep.length <= 2) {
      // Last separator is decimal (e.g. "5233.33" or "5233,33" or "1.964.000,50")
      const intPart = trimmed.substring(0, lastSep).replace(/[.,]/g, '');
      return parseFloat(`${intPart}.${afterSep}`);
    } else {
      // Last separator has 3+ digits after → thousand separator (e.g. "1.964.000")
      return parseFloat(trimmed.replace(/[.,]/g, ''));
    }
  };

  const parseCptyAmount = (value: string): number | undefined => {
    if (!value.trim()) return undefined;
    const parsed = parseLocalizedNumber(value);
    return isNaN(parsed) ? undefined : parsed;
  };

  // ── Open Email ──
  const handleOpenEmail = () => {
    for (const [, group] of Object.entries(groupedByBank)) {
      const maturityDate = formatDate(trades[0]?.MaturityDate);
      const lines: string[] = [];

      lines.push(t(`${prefix}.emailGreeting`));
      lines.push('');
      lines.push(t(`${prefix}.emailIntro`, { date: maturityDate }));
      lines.push('');

      // Collect all payment items
      interface CashflowItem {
        bankRef: string;
        tradeNumber: string;
        currency: string;
        amount: number;
        accountId: string;
        accountNumber: string;
        accountBankName: string;
      }
      const pagamos: CashflowItem[] = [];
      const recibimos: CashflowItem[] = [];

      for (const tr of group.trades) {
        const calc = ourCalculations.find(c => c.TradeNumber === tr.TradeNumber);
        const sel = accountSelections[tr.TradeNumber];
        const bankRef = getBankRef(tr);

        if (calc?.WePayAmount && calc.WePayAmount > 0 && sel?.cargarAccountId) {
          const acc = bankAccounts.find(a => a.id === sel.cargarAccountId);
          pagamos.push({
            bankRef,
            tradeNumber: tr.TradeNumber,
            currency: calc.WePayCurrency || '',
            amount: calc.WePayAmount,
            accountId: sel.cargarAccountId,
            accountNumber: acc?.accountNumber || '',
            accountBankName: acc?.bankName || '',
          });
        }

        if (calc?.WeReceiveAmount && calc.WeReceiveAmount > 0 && sel?.abonarAccountId) {
          const acc = bankAccounts.find(a => a.id === sel.abonarAccountId);
          recibimos.push({
            bankRef,
            tradeNumber: tr.TradeNumber,
            currency: calc.WeReceiveCurrency || '',
            amount: calc.WeReceiveAmount,
            accountId: sel.abonarAccountId,
            accountNumber: acc?.accountNumber || '',
            accountBankName: acc?.bankName || '',
          });
        }
      }

      const isSameBank = (accountBankName: string) => accountBankName === group.bankName;

      if (netPayments) {
        // Netted format
        const ledger: Record<string, {
          currency: string; accountId: string; accountNumber: string; accountBankName: string;
          payTotal: number; recvTotal: number;
          payTrades: { bankRef: string; tradeNumber: string; amount: number }[];
          recvTrades: { bankRef: string; tradeNumber: string; amount: number }[];
        }> = {};

        const getKey = (currency: string, accountId: string) => `${currency}::${accountId}`;

        for (const item of pagamos) {
          const key = getKey(item.currency, item.accountId);
          if (!ledger[key]) {
            ledger[key] = {
              currency: item.currency, accountId: item.accountId,
              accountNumber: item.accountNumber, accountBankName: item.accountBankName,
              payTotal: 0, recvTotal: 0, payTrades: [], recvTrades: [],
            };
          }
          ledger[key].payTotal += item.amount;
          ledger[key].payTrades.push({ bankRef: item.bankRef, tradeNumber: item.tradeNumber, amount: item.amount });
        }

        for (const item of recibimos) {
          const key = getKey(item.currency, item.accountId);
          if (!ledger[key]) {
            ledger[key] = {
              currency: item.currency, accountId: item.accountId,
              accountNumber: item.accountNumber, accountBankName: item.accountBankName,
              payTotal: 0, recvTotal: 0, payTrades: [], recvTrades: [],
            };
          }
          ledger[key].recvTotal += item.amount;
          ledger[key].recvTrades.push({ bankRef: item.bankRef, tradeNumber: item.tradeNumber, amount: item.amount });
        }

        for (const entry of Object.values(ledger)) {
          const net = entry.recvTotal - entry.payTotal;
          const allTrades = [...entry.payTrades, ...entry.recvTrades];
          const sameBank = isSameBank(entry.accountBankName);

          if (net > 0) {
            lines.push(`Neto a recibir: ${entry.currency} ${fmt(net)} - por favor abonar cuenta ${entry.accountNumber}, ${entry.accountBankName}.`);
          } else if (net < 0) {
            const amt = `${entry.currency} ${fmt(Math.abs(net))}`;
            if (sameBank) {
              lines.push(`Neto a pagar: ${amt} - por favor cargar cuenta ${entry.accountNumber}.`);
            } else {
              lines.push(`Neto a pagar: ${amt} - realizaremos una transferencia de ${amt} a cuenta ${entry.accountNumber}, ${entry.accountBankName}.`);
            }
          } else {
            lines.push(`Neto: ${entry.currency} 0 (sin movimiento) - cuenta ${entry.accountNumber}, ${entry.accountBankName}.`);
          }

          for (const tr of allTrades) {
            const isPay = entry.payTrades.some(p => p.tradeNumber === tr.tradeNumber);
            const direction = isPay ? 'Pagamos' : 'Recibimos';
            lines.push(`  - Operaci\u00f3n ${tr.bankRef || tr.tradeNumber} (Nuestra ref. ${tr.tradeNumber}): ${direction} ${entry.currency} ${fmt(tr.amount)}`);
          }
          lines.push('');
        }
      } else {
        // Non-netted format
        if (pagamos.length > 0) {
          lines.push('Pagamos:');
          for (const item of pagamos) {
            const ref = item.bankRef
              ? `${item.bankRef} (Nuestra ref. ${item.tradeNumber})`
              : item.tradeNumber;
            const amt = `${item.currency} ${fmt(item.amount)}`;
            if (isSameBank(item.accountBankName)) {
              lines.push(`- Operaci\u00f3n n\u00famero ${ref} - por favor cargar cuenta ${item.accountNumber}, ${amt}.`);
            } else {
              lines.push(`- Operaci\u00f3n n\u00famero ${ref} - realizaremos una transferencia de ${amt} a cuenta ${item.accountNumber}, ${item.accountBankName}.`);
            }
          }
          lines.push('');
        }

        if (recibimos.length > 0) {
          lines.push('Recibimos:');
          for (const item of recibimos) {
            const ref = item.bankRef
              ? `${item.bankRef} (Nuestra ref. ${item.tradeNumber})`
              : item.tradeNumber;
            lines.push(`- Operaci\u00f3n n\u00famero ${ref} - por favor abonar cuenta ${item.accountNumber}, ${item.accountBankName}, ${item.currency} ${fmt(item.amount)}.`);
          }
          lines.push('');
        }
      }

      // Closing
      lines.push(t(`${prefix}.emailClosing`, { organizationName }));

      const body = lines.join('\n');
      const subject = t(`${prefix}.emailSubject`, { date: maturityDate, organizationName });

      const ccValue = trades[0]?.expiryCc || '';
      const ccParam = ccValue ? `&cc=${encodeURIComponent(ccValue)}` : '';
      const mailto = `mailto:${group.bankEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}${ccParam}`;
      window.open(mailto, '_blank');
    }

  };

  // ── Generate Settlement Instructions ──
  const handleGenerateSI = async () => {
    setSiGenerating(true);
    try {
      const paths: Record<string, string> = {};
      for (const trade of trades) {
        const preview = accountPreviews[trade.TradeNumber];
        const sel = accountSelections[trade.TradeNumber];

        if (preview?.ruleMatched && preview?.templateId) {
          try {
            const result = await clientService.generateSettlementInstruction(
              clientId,
              trade.TradeNumber,
              trade.CounterpartySettlement?.CounterpartyTradeNumber,
              undefined,
              sel?.cargarAccountId,
              sel?.abonarAccountId,
              'ON_EXPIRY',
            );

            if (result.success && result.data?.storage_path) {
              paths[trade.id] = result.data.storage_path;

              try {
                const urlResp = await clientService.getSettlementInstructionUrl(
                  clientId, undefined, result.data.storage_path
                );
                if (urlResp.success && urlResp.data?.signed_url) {
                  const a = document.createElement('a');
                  a.href = urlResp.data.signed_url;
                  a.download = '';
                  a.style.display = 'none';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              } catch (dlErr) {
                logger.error(`Failed to download SI for trade ${trade.TradeNumber}:`, dlErr);
              }
            }
          } catch (err) {
            logger.error(`Failed to generate SI for trade ${trade.TradeNumber}:`, err);
          }
        }
      }
      setSiDone(true);
    } finally {
      setSiGenerating(false);
    }
  };

  // ── Confirm & Close ──
  const handleConfirmAndClose = async () => {
    setLoading(true);
    try {
      // Split trades into groups
      const notifyIds: string[] = [];       // pending trades not marked agreed → 'notified'
      const directAgreedIds: string[] = []; // trades marked agreed with no amount changes → 'agreed'
      const manualAcceptTrades: { tradeId: string; wePayAmount?: number; weReceiveAmount?: number }[] = [];

      for (const trade of trades) {
        const isMarkedAgreed = agreedTradeIds.has(trade.TradeNumber);
        const cptyEntry = cptyAmounts[trade.TradeNumber];
        const cptyPay = cptyEntry ? parseCptyAmount(cptyEntry.wePay) : undefined;
        const cptyRecv = cptyEntry ? parseCptyAmount(cptyEntry.weReceive) : undefined;
        const hasCptyAmounts = cptyPay !== undefined || cptyRecv !== undefined;

        if (isMarkedAgreed) {
          if (hasCptyAmounts) {
            // Agreed with different amounts → manual accept
            const calc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
            const ourPay = calc?.WePayAmount || 0;
            const ourRecv = calc?.WeReceiveAmount || 0;
            const payDiffers = cptyPay !== undefined && Math.abs(cptyPay - ourPay) > 0.001;
            const recvDiffers = cptyRecv !== undefined && Math.abs(cptyRecv - ourRecv) > 0.001;

            if (payDiffers || recvDiffers) {
              manualAcceptTrades.push({
                tradeId: trade.id,
                wePayAmount: cptyPay ?? ourPay,
                weReceiveAmount: cptyRecv ?? ourRecv,
              });
            } else {
              directAgreedIds.push(trade.id);
            }
          } else {
            directAgreedIds.push(trade.id);
          }
        } else {
          notifyIds.push(trade.id);
        }
      }

      // Build account selections map for persistence
      const acctSelectionsForBackend: Record<string, { cargarAccountId?: string; abonarAccountId?: string }> = {};
      for (const trade of trades) {
        const sel = accountSelections[trade.TradeNumber];
        if (sel && (sel.cargarAccountId || sel.abonarAccountId)) {
          acctSelectionsForBackend[trade.id] = sel;
        }
      }

      // Execute API calls
      if (notifyIds.length > 0) {
        await clientService.updateExpiryState(clientId, notifyIds, 'notified', acctSelectionsForBackend);
      }
      if (directAgreedIds.length > 0) {
        await clientService.updateExpiryState(clientId, directAgreedIds, 'agreed');
      }
      if (manualAcceptTrades.length > 0) {
        await clientService.manualAcceptAmounts(clientId, manualAcceptTrades);
      }

      const allAgreedIds = [...directAgreedIds, ...manualAcceptTrades.map(t => t.tradeId)];
      onSuccess(notifyIds, allAgreedIds, manualAcceptTrades);
    } catch (err) {
      logger.error('Expiry notification confirm flow failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <UploadingModal isOpen={loading} message={t(`${prefix}.generating`)} />
    <div className="exceptions-modal-overlay" onClick={onClose}>
      <div className="exceptions-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1050px' }}>

        {/* Header */}
        <div className="exceptions-modal-header">
          <div className="header-title-area">
            <Send size={22} color="var(--accent-blue)" />
            <h2>{t(`${prefix}.title`)}</h2>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="exceptions-content" style={{ minHeight: 'auto', padding: '20px 24px' }}>

          {Object.entries(groupedByBank).map(([bankId, group]) => (
            <div key={bankId} style={{ marginBottom: '1.5rem' }}>
              {/* Bank header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <Mail size={14} color="var(--text-secondary)" />
                <strong style={{ color: 'var(--text-primary)' }}>{group.bankName}</strong>
                {group.bankEmail ? (
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>({group.bankEmail})</span>
                ) : (
                  <span style={{ color: '#dc3545', fontSize: '12px' }}>
                    ({t(`${prefix}.noBankEmail`, { counterparty: group.bankName })})
                  </span>
                )}
              </div>

              {/* Bulk account selector */}
              {(() => {
                const groupTrades = group.trades;
                const slots: { side: 'cargar' | 'abonar'; currency: string; counterpartyId?: string }[] = [];
                const seen = new Set<string>();
                groupTrades.forEach(trade => {
                  const calc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                  const req = getRequiredAccounts(trade, calc);
                  if (req.needsCargar && req.cargarCurrency) {
                    const k = `cargar-${req.cargarCurrency}`;
                    if (!seen.has(k)) { seen.add(k); slots.push({ side: 'cargar', currency: req.cargarCurrency, counterpartyId: trade.CounterpartyId }); }
                  }
                  if (req.needsAbonar && req.abonarCurrency) {
                    const k = `abonar-${req.abonarCurrency}`;
                    if (!seen.has(k)) { seen.add(k); slots.push({ side: 'abonar', currency: req.abonarCurrency }); }
                  }
                });

                if (slots.length === 0) return null;

                return (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '6px 8px', marginBottom: '6px',
                    backgroundColor: 'rgba(100, 149, 237, 0.06)',
                    borderRadius: '4px', border: '1px solid rgba(100, 149, 237, 0.15)',
                    fontSize: '12px',
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {t(`${prefix}.applyToAll`)}:
                    </span>
                    {slots.map(slot => {
                      const filtered = bankAccounts.filter(a => {
                        if (a.accountCurrency !== slot.currency) return false;
                        const type = a.accountType || 'nostro';
                        if (slot.side === 'abonar') return type === 'nostro';
                        if (type === 'nostro') return true;
                        return type === 'vostro' && a.counterpartyId === slot.counterpartyId;
                      });
                      const label = slot.side === 'cargar'
                        ? `${t(`${prefix}.cargarAccount`)} (${slot.currency})`
                        : `${t(`${prefix}.abonarAccount`)} (${slot.currency})`;
                      const key = slot.side === 'cargar' ? 'cargarAccountId' : 'abonarAccountId';

                      return (
                        <div key={`bulk-${slot.side}-${slot.currency}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>{label}</span>
                          <select
                            value=""
                            onChange={(e) => {
                              if (!e.target.value) return;
                              const accountId = e.target.value;
                              setAccountSelections(prev => {
                                const next = { ...prev };
                                groupTrades.forEach(trade => {
                                  const calc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                                  const req = getRequiredAccounts(trade, calc);
                                  const matchesSide = slot.side === 'cargar' ? req.needsCargar && req.cargarCurrency === slot.currency
                                    : req.needsAbonar && req.abonarCurrency === slot.currency;
                                  if (matchesSide) {
                                    next[trade.TradeNumber] = { ...next[trade.TradeNumber], [key]: accountId };
                                  }
                                });
                                return next;
                              });
                            }}
                            style={{
                              padding: '2px 4px', fontSize: '11px', borderRadius: '3px',
                              border: '1px solid var(--border-color)',
                              backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)',
                              maxWidth: '220px',
                            }}
                          >
                            <option value="">{t(`${prefix}.selectAccount`)}</option>
                            {filtered.map(acc => {
                              const typeLabel = (acc.accountType || 'nostro') === 'vostro' ? ' [Vostro]' : '';
                              return (
                                <option key={acc.id} value={acc.id}>
                                  {acc.bankName} - {acc.accountNumber} ({acc.accountCurrency}){typeLabel}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Trades table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {t(`${prefix}.bankRef`)}
                    </th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {t(`${prefix}.ourRef`)}
                    </th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {t(`${prefix}.wePay`)}
                    </th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {t(`${prefix}.weReceive`)}
                    </th>
                    {hasNotifiedTrades && (
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500, minWidth: '130px' }}>
                        {t(`${prefix}.cptyAmount`)}
                      </th>
                    )}
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500, minWidth: '180px' }}>
                      {t(`${prefix}.bankAccounts`)}
                    </th>
                    {hasNotifiedTrades && (
                      <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500, width: '80px' }}>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {group.trades.map((trade) => {
                    const calc = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                    const req = getRequiredAccounts(trade, calc);
                    const isAgreed = agreedTradeIds.has(trade.TradeNumber);
                    const isNotified = trade.expiryState === 'notified';
                    const cptyEntry = cptyAmounts[trade.TradeNumber] || { wePay: '', weReceive: '' };

                    // Determine if cpty amounts differ from ours
                    const cptyPay = parseCptyAmount(cptyEntry.wePay);
                    const cptyRecv = parseCptyAmount(cptyEntry.weReceive);
                    const payDiffers = cptyPay !== undefined && Math.abs(cptyPay - (calc?.WePayAmount || 0)) > 0.001;
                    const recvDiffers = cptyRecv !== undefined && Math.abs(cptyRecv - (calc?.WeReceiveAmount || 0)) > 0.001;

                    return (
                      <tr key={trade.id} style={{
                        borderBottom: '1px solid var(--border-color)',
                        backgroundColor: isAgreed ? 'rgba(40, 167, 69, 0.05)' : undefined,
                      }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text-primary)', fontWeight: 500 }}>
                          {getBankRef(trade)}
                        </td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>
                          {trade.TradeNumber}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-primary)' }}>
                          {calc?.WePayAmount ? `${calc.WePayCurrency} ${fmt(calc.WePayAmount)}` : '\u2014'}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-primary)' }}>
                          {calc?.WeReceiveAmount ? `${calc.WeReceiveCurrency} ${fmt(calc.WeReceiveAmount)}` : '\u2014'}
                        </td>
                        {hasNotifiedTrades && (
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            {isNotified ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                                {(calc?.WePayAmount && calc.WePayAmount > 0) ? (
                                  <input
                                    type="text"
                                    value={cptyEntry.wePay}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || /^[\d.,\s]+$/.test(val)) {
                                        setCptyAmounts(prev => ({
                                          ...prev,
                                          [trade.TradeNumber]: { ...prev[trade.TradeNumber] || { wePay: '', weReceive: '' }, wePay: val },
                                        }));
                                      }
                                    }}
                                    onFocus={(e) => {
                                      // Strip formatting on focus for easy editing
                                      const parsed = parseLocalizedNumber(e.target.value);
                                      if (!isNaN(parsed)) {
                                        const raw = String(parsed);
                                        setCptyAmounts(prev => ({
                                          ...prev,
                                          [trade.TradeNumber]: { ...prev[trade.TradeNumber] || { wePay: '', weReceive: '' }, wePay: raw },
                                        }));
                                      }
                                    }}
                                    onBlur={() => {
                                      const parsed = parseLocalizedNumber(cptyEntry.wePay);
                                      if (!isNaN(parsed)) {
                                        setCptyAmounts(prev => ({
                                          ...prev,
                                          [trade.TradeNumber]: { ...prev[trade.TradeNumber] || { wePay: '', weReceive: '' }, wePay: fmtInput(parsed) },
                                        }));
                                      }
                                    }}
                                    style={{
                                      width: '130px',
                                      padding: '2px 6px',
                                      fontSize: '12px',
                                      textAlign: 'right',
                                      borderRadius: '4px',
                                      border: `1px solid ${payDiffers ? '#e67e00' : 'var(--border-color)'}`,
                                      backgroundColor: payDiffers ? 'rgba(230, 126, 0, 0.05)' : 'var(--bg-primary)',
                                      color: payDiffers ? '#e67e00' : 'var(--text-primary)',
                                      fontWeight: payDiffers ? 600 : 400,
                                    }}
                                  />
                                ) : null}
                                {(calc?.WeReceiveAmount && calc.WeReceiveAmount > 0) ? (
                                  <input
                                    type="text"
                                    value={cptyEntry.weReceive}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || /^[\d.,\s]+$/.test(val)) {
                                        setCptyAmounts(prev => ({
                                          ...prev,
                                          [trade.TradeNumber]: { ...prev[trade.TradeNumber] || { wePay: '', weReceive: '' }, weReceive: val },
                                        }));
                                      }
                                    }}
                                    onFocus={(e) => {
                                      const parsed = parseLocalizedNumber(e.target.value);
                                      if (!isNaN(parsed)) {
                                        const raw = String(parsed);
                                        setCptyAmounts(prev => ({
                                          ...prev,
                                          [trade.TradeNumber]: { ...prev[trade.TradeNumber] || { wePay: '', weReceive: '' }, weReceive: raw },
                                        }));
                                      }
                                    }}
                                    onBlur={() => {
                                      const parsed = parseLocalizedNumber(cptyEntry.weReceive);
                                      if (!isNaN(parsed)) {
                                        setCptyAmounts(prev => ({
                                          ...prev,
                                          [trade.TradeNumber]: { ...prev[trade.TradeNumber] || { wePay: '', weReceive: '' }, weReceive: fmtInput(parsed) },
                                        }));
                                      }
                                    }}
                                    style={{
                                      width: '130px',
                                      padding: '2px 6px',
                                      fontSize: '12px',
                                      textAlign: 'right',
                                      borderRadius: '4px',
                                      border: `1px solid ${recvDiffers ? '#e67e00' : 'var(--border-color)'}`,
                                      backgroundColor: recvDiffers ? 'rgba(230, 126, 0, 0.05)' : 'var(--bg-primary)',
                                      color: recvDiffers ? '#e67e00' : 'var(--text-primary)',
                                      fontWeight: recvDiffers ? 600 : 400,
                                    }}
                                  />
                                ) : null}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{'\u2014'}</span>
                            )}
                          </td>
                        )}
                        <td style={{ padding: '6px 8px' }}>
                          {previewLoading ? (
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              <Loader2 size={12} className="spin" style={{ display: 'inline', marginRight: '4px' }} />
                              {t(`${prefix}.loadingAccounts`)}
                            </span>
                          ) : (
                            <>
                              {req.needsCargar && renderAccountDropdown(
                                trade.TradeNumber, 'cargar', req.cargarCurrency,
                                req.needsCargar && req.needsAbonar,
                                trade.CounterpartyId,
                              )}
                              {req.needsAbonar && renderAccountDropdown(
                                trade.TradeNumber, 'abonar', req.abonarCurrency,
                                req.needsCargar && req.needsAbonar,
                              )}
                            </>
                          )}
                        </td>
                        {hasNotifiedTrades && (
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            {isNotified ? (
                              isAgreed ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                  <span style={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: '#28a745',
                                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                  }}>
                                    {t(`${prefix}.markAgreed`)}
                                  </span>
                                  <button
                                    onClick={() => setAgreedTradeIds(prev => { const next = new Set(prev); next.delete(trade.TradeNumber); return next; })}
                                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', padding: 0 }}
                                  >
                                    {t(`${prefix}.undoAgreed`)}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setAgreedTradeIds(prev => { const next = new Set(prev); next.add(trade.TradeNumber); return next; })}
                                  style={{
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: '#28a745',
                                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                    border: '1px solid rgba(40, 167, 69, 0.3)',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {t(`${prefix}.markAgreed`)} ✓
                                </button>
                              )
                            ) : (
                              <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>{'\u2014'}</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {/* Totals row per bank */}
                  {group.trades.length > 1 && (() => {
                    // Group totals by currency
                    const payTotals: Record<string, number> = {};
                    const recvTotals: Record<string, number> = {};
                    for (const tr of group.trades) {
                      const calc = ourCalculations.find(c => c.TradeNumber === tr.TradeNumber);
                      if (calc?.WePayAmount && calc.WePayAmount > 0) {
                        const ccy = calc.WePayCurrency || '';
                        payTotals[ccy] = (payTotals[ccy] || 0) + calc.WePayAmount;
                      }
                      if (calc?.WeReceiveAmount && calc.WeReceiveAmount > 0) {
                        const ccy = calc.WeReceiveCurrency || '';
                        recvTotals[ccy] = (recvTotals[ccy] || 0) + calc.WeReceiveAmount;
                      }
                    }

                    return (
                      <tr style={{ borderTop: '2px solid var(--border-color)', fontWeight: 700 }}>
                        <td colSpan={2} style={{ padding: '6px 8px', color: 'var(--text-primary)' }}>
                          {t(`${prefix}.total`)}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-primary)' }}>
                          {Object.entries(payTotals).map(([ccy, total]) => (
                            <div key={ccy}>{ccy} {fmt(total)}</div>
                          ))}
                          {Object.keys(payTotals).length === 0 && '\u2014'}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-primary)' }}>
                          {Object.entries(recvTotals).map(([ccy, total]) => (
                            <div key={ccy}>{ccy} {fmt(total)}</div>
                          ))}
                          {Object.keys(recvTotals).length === 0 && '\u2014'}
                        </td>
                        {hasNotifiedTrades && <td></td>}
                        <td></td>
                        {hasNotifiedTrades && <td></td>}
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '16px 24px',
          borderTop: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
          borderRadius: '0 0 12px 12px',
        }}>
          {/* Netting checkbox */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={netPayments}
              onChange={(e) => setNetPayments(e.target.checked)}
              disabled={trades.length < 2}
            />
            {t(`${prefix}.netPayments`)}
          </label>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={onClose}
              disabled={loading || siGenerating}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {t(`${prefix}.cancel`)}
            </button>

            {/* Open Email */}
            <button
              onClick={handleOpenEmail}
              disabled={!allAccountsSelected || loading || siGenerating}
              style={{
                padding: '8px 16px',
                backgroundColor: !allAccountsSelected ? 'var(--text-secondary)' : 'var(--accent-blue)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: !allAccountsSelected || loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 600,
                fontSize: '14px',
                opacity: !allAccountsSelected ? 0.6 : 1,
              }}
            >
              <Mail size={16} />
              {t(`${prefix}.openEmail`)}
            </button>

            {/* Generate SI — only if any trades have templates */}
            {trades.some(tr => accountPreviews[tr.TradeNumber]?.templateId) && (
              <button
                onClick={handleGenerateSI}
                disabled={!allAccountsSelected || siGenerating || loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: siDone ? '#28a745' : '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: !allAccountsSelected || siGenerating ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                {siGenerating ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    {t(`${prefix}.generating`)}
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    {siDone
                      ? t(`${prefix}.siGenerated`)
                      : t(`${prefix}.generateSI`)
                    }
                  </>
                )}
              </button>
            )}

            {/* Confirm / Mark Notified */}
            <button
              onClick={handleConfirmAndClose}
              disabled={loading || siGenerating}
              style={{
                padding: '8px 16px',
                backgroundColor: hasNotifiedTrades ? '#28a745' : '#e67e00',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="spin" />
                  {hasNotifiedTrades ? t(`${prefix}.confirming`) : t(`${prefix}.marking`)}
                </>
              ) : (
                <>
                  {hasNotifiedTrades ? <CheckCircle size={16} /> : <Send size={16} />}
                  {hasNotifiedTrades ? t(`${prefix}.confirmAndClose`) : t(`${prefix}.markNotified`)}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ExpiryNotificationModal;
